/**
 * The authenticated app shell — docs/08-frontend-and-ux.md §2.2 and §9.4.
 *
 * Nav entries whose screens land in later milestones are rendered disabled rather
 * than hidden: the information architecture is the doc's, and a dead link is a
 * worse lie than a visibly-not-yet-there one.
 */

import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from './session.js';
import { useUnreadCount } from '../features/notifications/NotificationsPage.js';
import styles from './AppShell.module.css';
import bell from '../features/notifications/notifications.module.css';
import ui from '../ui/components.module.css';

interface NavEntry {
  to: string;
  labelKey: string;
  /** Screens delivered by a later milestone (docs/12 §4). */
  milestone?: string;
}

const NAV: NavEntry[] = [
  { to: '/', labelKey: 'nav.home' },
  { to: '/tree', labelKey: 'nav.tree' },
  { to: '/people', labelKey: 'nav.people' },
  { to: '/kinship', labelKey: 'nav.kinship' },
  { to: '/photos/upload', labelKey: 'nav.photos' },
  { to: '/proposals', labelKey: 'nav.proposals' },
];

/** Admin-only rail entries (docs/08 §2.2, §3.11). */
const ADMIN_NAV: NavEntry[] = [
  { to: '/council', labelKey: 'nav.council' },
  { to: '/admin/audit', labelKey: 'nav.audit' },
  { to: '/admin/settings', labelKey: 'nav.adminSettings' },
];

export function AppShell(): JSX.Element {
  const { t } = useTranslation();
  const { user, signOut } = useSession();
  const unread = useUnreadCount();
  const nav = user?.role === 'admin' ? [...NAV, ...ADMIN_NAV] : NAV;

  return (
    <div className={styles.shell}>
      <a className={styles.skipLink} href="#content">
        {t('nav.skipToContent')}
      </a>

      <header className={styles.topbar}>
        <NavLink className={styles.brand} to="/">
          {t('app.name')}
        </NavLink>
        <span className={styles.spacer} />
        <NavLink className={bell.bell} to="/notifications">
          {t('nav.notifications')}
          {unread > 0 && (
            <span className={bell.badge} aria-label={t('notifications.unread', { count: unread })}>
              {unread}
            </span>
          )}
        </NavLink>
        {user && <span className={ui.muted}>{user.display_name}</span>}
        <button className={ui.buttonSecondary} type="button" onClick={() => void signOut()}>
          {t('nav.signOut')}
        </button>
      </header>

      <div className={styles.body}>
        <nav className={styles.rail} aria-label={t('nav.home')}>
          <ul className={styles.railList}>
            {nav.map((entry) =>
              entry.milestone ? (
                <li key={entry.to}>
                  <span
                    className={styles.railLinkDisabled}
                    aria-disabled="true"
                    title={entry.milestone}
                  >
                    {t(entry.labelKey)}
                  </span>
                </li>
              ) : (
                <li key={entry.to}>
                  <NavLink className={styles.railLink} to={entry.to} end>
                    {t(entry.labelKey)}
                  </NavLink>
                </li>
              ),
            )}
          </ul>
        </nav>

        <main className={styles.content} id="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
